/**
 * unloadFlush — LAST-GASP durable capture as the page is hidden/unloaded.
 *
 * FR-FE-IRONCLAD-TELEMETRY-OUTBOX. The normal drain (queueFlush) runs on an interval
 * and on `online`. But a rep who submits a report and immediately backgrounds or kills
 * the PWA can die between the failed POST and the next 30s flush — the payload is safe
 * in IndexedDB (persist-before-network), yet it has not reached the off-device sink. On
 * the way out we make a BEST-EFFORT push of every still-pending outbox payload to the
 * durable scout_activity_log sink, so it is recoverable off-device even if this browser
 * is never opened again.
 *
 * THREE deliberate constraints:
 *   1. Bind `pagehide` + `visibilitychange(hidden)` — NEVER `beforeunload`/`unload`.
 *      The latter block bfcache and are unreliable on mobile Safari (the rep's platform);
 *      `pagehide` is the standard, bfcache-safe unload signal.
 *   2. Reuse the proven keepalive-fetch shape from useActivityTracker.flusher
 *      (`keepalive:true` so the request survives navigation, Bearer auth because the
 *      durable sink is RLS'd, and the 64KB synchronous-throw guard).
 *   3. A beacon is BEST-EFFORT and is NEVER treated as durability: it does NOT delete
 *      the outbox entries. Only a CONFIRMED replay ack (queueFlush) removes an entry.
 *      A beacon that never lands, or lands and is lost, must leave the rep's copy exactly
 *      where it was (D-33 R1 — a best-effort success is not a confirmation).
 */

import { readQueue } from './queueStore'
import { WRITE_SAVE_FAILED_EVENT } from './failedWriteCapture'
import { getSupabaseSession } from '../utils/scoutLog'

/** keepalive-fetch payload ceiling: the browser rejects a keepalive body over 64KB. */
const KEEPALIVE_MAX_BYTES = 64 * 1024

let bound = false
let boundHandler: (() => void) | null = null
let boundVisibility: (() => void) | null = null

/**
 * POST the pending outbox payloads to the durable sink with a keepalive fetch.
 *
 * Mirrors `createFlusher().flushBeacon` (useActivityTracker.flusher.ts): keepalive so
 * the request outlives the page, Bearer auth (sendBeacon cannot set Authorization, and
 * the sink is RLS'd), and the 64KB guard — a keepalive fetch THROWS synchronously when
 * the body exceeds the limit, which must be caught, not propagated into the unload path.
 *
 * Returns nothing and asserts nothing about delivery: the caller does NOT delete on the
 * strength of this. It is loud on failure (console.warn) but never throws.
 */
function beaconPending(events: unknown[]): void {
  if (events.length === 0) return

  const session = getSupabaseSession()
  if (!session) {
    // No session — cannot authenticate the durable write. LOUD, not silent: the payload
    // stays in the outbox (untouched) for the next authenticated flush.
    console.warn('[unloadFlush] no authenticated session on unload — outbox NOT flushed off-device, entries RETAINED')
    return
  }

  const supabaseUrl = ((import.meta.env.VITE_SUPABASE_URL || '') as string).replace(/\/$/, '')
  const anonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY || '') as string
  if (!supabaseUrl || !anonKey) {
    console.warn('[unloadFlush] Supabase config missing on unload — outbox NOT flushed off-device, entries RETAINED')
    return
  }

  const body = JSON.stringify(events)
  // Guard the 64KB keepalive ceiling BEFORE the fetch: an oversized keepalive body throws
  // synchronously (see the flusher). We would rather skip the beacon (the entries are
  // still safely on-device) than let a throw escape into the page-hide handler.
  if (typeof Blob === 'function' ? new Blob([body]).size > KEEPALIVE_MAX_BYTES : body.length > KEEPALIVE_MAX_BYTES) {
    console.warn('[unloadFlush] pending outbox exceeds the 64KB keepalive limit — skipped, entries RETAINED for the next flush')
    return
  }

  try {
    void fetch(`${supabaseUrl}/rest/v1/scout_activity_log`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': anonKey,
        'Authorization': `Bearer ${session.accessToken}`,
        'Prefer': 'return=minimal',
      },
      body,
      keepalive: true,
    }).catch((err: unknown) => {
      // Best-effort: a rejected keepalive fetch does NOT lose data — the entries remain
      // in the outbox. Surface it so a broken sink is visible (D-33 R1).
      console.warn('[unloadFlush] keepalive beacon rejected — entries RETAINED for the next flush', err)
    })
  } catch (err) {
    // keepalive fetch can throw synchronously (payload too large / runtime constraint).
    // The entries are untouched on-device; never let this take down the unload path.
    console.warn('[unloadFlush] keepalive beacon threw — entries RETAINED for the next flush', err)
  }
}

/**
 * Read the still-pending outbox and beacon its payloads to the durable sink. Best-effort:
 * it NEVER deletes an entry (only a confirmed replay ack does). The IndexedDB read is
 * async and may not complete if the page dies first — that is the accepted best-effort
 * limit; the entries are already durable on-device regardless.
 */
async function flushPendingOnUnload(): Promise<void> {
  const session = getSupabaseSession()
  const userId = session?.userId ?? null
  const queue = await readQueue()
  const pending = queue.filter(e => e.status === 'pending')
  if (pending.length === 0) return

  const events = pending.map(e => ({
    user_id: userId,
    event: WRITE_SAVE_FAILED_EVENT,
    payload: {
      kind: e.type,
      reason: 'unload-beacon',
      id: e.id,
      retries: e.retries,
      createdAt: e.createdAt,
      payload: e.payload,
    },
    route: typeof window !== 'undefined' ? window.location.pathname : null,
    created_at: new Date().toISOString(),
  }))

  beaconPending(events)
}

/**
 * Start the unload flush. Idempotent (safe to call from every mount point). Binds
 * `pagehide` and `visibilitychange(hidden)` — the bfcache-safe unload signals — and on
 * each fires a best-effort durable beacon of the pending outbox. Wire it once at app init
 * alongside `startOfflineQueue()`.
 */
export function startUnloadFlush(): void {
  if (bound) return
  if (typeof window === 'undefined') return
  bound = true

  boundHandler = () => { void flushPendingOnUnload() }
  boundVisibility = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      void flushPendingOnUnload()
    }
  }

  window.addEventListener('pagehide', boundHandler)
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', boundVisibility)
  }
}

/** Stop the unload flush and unbind. */
export function stopUnloadFlush(): void {
  if (!bound) return
  if (typeof window !== 'undefined' && boundHandler) {
    window.removeEventListener('pagehide', boundHandler)
  }
  if (typeof document !== 'undefined' && boundVisibility) {
    document.removeEventListener('visibilitychange', boundVisibility)
  }
  boundHandler = null
  boundVisibility = null
  bound = false
}
